import {
  Clock,
  Edit,
  FileQuestion,
  Filter,
  FolderOpen,
  FolderPlus,
  MoreVertical,
  Package,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";

import ConfirmDialog from "@/components/common/ConfirmDialog";
import { useAppSelector } from "@/hooks/redux";
import { orderService } from "@/services/MenuService";
import { menuCategoryService } from "@/services/MenuService";

import AddCategoryModal from "./modals/AddCategoryModal";
import AddItemModal from "./modals/AddItemModal";
import AddOrderModal from "./modals/AddOrderModal";
import CategoryEditModal from "./modals/CategoryEditModal";
import CategoryViewModal from "./modals/CategoryViewModal";
import ItemDetailsModal from "./modals/ItemDetailsModal";
import ItemEditModal from "./modals/ItemEditModal";
import OrderEditModal from "./modals/OrderEditModal";
import OrderViewModal from "./modals/OrderViewModal";

interface MenuItem {
  category: string;
  description: string;
  id: string;
  image?: string;
  images?: string[];
  isAvailable: boolean;
  name: string;
  price: number;
}

const sampleMenuItems: MenuItem[] = [
  {
    category: "Pizza",
    description: "Fresh tomatoes, mozzarella, and basil",
    id: "1",
    isAvailable: true,
    name: "Margherita Pizza",
    price: 18.5,
  },
  {
    category: "Salads",
    description: "Romaine lettuce, parmesan, croutons, caesar dressing",
    id: "2",
    isAvailable: true,
    name: "Caesar Salad",
    price: 12.99,
  },
  {
    category: "Pasta",
    description: "Grilled chicken with creamy alfredo sauce over fettuccine",
    id: "3",
    isAvailable: false,
    name: "Chicken Alfredo",
    price: 22.5,
  },
];

const RestaurantManagement: React.FC = () => {
  // Helper to build full image URL from API response
  const getImageUrl = (imgPath: string) => {
    if (!imgPath) return "";
    const BASE_URL = import.meta.env.VITE_API_STORAGE_URL || "";
    if (imgPath.startsWith("http")) return imgPath;
    // Prepend /storage/ for correct public path
    return (
      BASE_URL.replace(/\/$/, "") + "/storage/" + imgPath.replace(/^\//, "")
    );
  };
  const [activeTab, setActiveTab] = useState("items");
  const location = useLocation();

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const tab = params.get("tab");
      if (tab) setActiveTab(tab);
    } catch (err) {
      // ignore
    }
  }, [location.search]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  // Store full category objects
  const [categories, setCategories] = useState<any[]>([]);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [viewItem, setViewItem] = useState<MenuItem | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [editItem, setEditItem] = useState<MenuItem | null>(null);
  const [itemView, setItemView] = useState<"grid" | "table">("grid");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [itemSearch, setItemSearch] = useState("");
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [viewCategory, setViewCategory] = useState<null | string>(null);
  const [editCategory, setEditCategory] = useState<any | null>(null);
  const [deleteCategory, setDeleteCategory] = useState<any | null>(null);
  const [showDeleteCategoryConfirm, setShowDeleteCategoryConfirm] =
    useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [categoryView, setCategoryView] = useState<"grid" | "table">("grid");
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [viewOrder, setViewOrder] = useState<any | null>(null);
  const [editOrder, setEditOrder] = useState<any | null>(null);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("");
  const [orderView, setOrderView] = useState<"grid" | "table">("grid");

  // Menu item delete dialog
  const [deleteItem, setDeleteItem] = useState<MenuItem | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Order delete dialog
  const [deleteOrder, setDeleteOrder] = useState<any | null>(null);
  const [showDeleteOrderConfirm, setShowDeleteOrderConfirm] = useState(false);

  // Get logged-in user from Redux
  const user = useAppSelector((state) => state.auth.user);

  React.useEffect(() => {
    if (!user?.id) return;
    setLoadingMenu(true);
    import("@/services/MenuService").then(({ default: MenuService }) => {
      MenuService.getMenuItemsByUser(user.id)
        .then((res: any) => {
          setMenuItems(
            res.data.map((item: any) => ({
              ...item,
              isAvailable: item.available === 1,
            })),
          );
        })
        .catch((err: any) => {
          console.error("Failed to fetch menu items:", err);
        })
        .finally(() => setLoadingMenu(false));
    });
  }, [user?.id]);

  React.useEffect(() => {
    if (!user?.id) return;
    setLoadingCategories(true);
    menuCategoryService
      .getCategories({ uploaded_by: user.id })
      .then((res: any) => {
        setCategories(res.data);
      })
      .catch((err: any) => {
        console.error("Failed to fetch categories:", err);
      })
      .finally(() => setLoadingCategories(false));
  }, [user?.id]);

  const [orders, setOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Fetch orders from backend for logged-in user
  React.useEffect(() => {
    if (!user?.id) return;
    setLoadingOrders(true);
    orderService
      .getOrders({ created_by: user.id })
      .then((res: any) => {
        setOrders(res.data);
      })
      .catch((err: any) => {
        console.error("Failed to fetch orders:", err);
      })
      .finally(() => setLoadingOrders(false));
  }, [user?.id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
      case "available":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "inactive":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  const tabs = [
    { icon: Package, id: "items", label: "Menu Items" },
    { icon: Filter, id: "categories", label: "Categories" },
    { icon: Clock, id: "orders", label: "Orders" },
  ];

  const filteredMenuItems = menuItems
    .filter(
      (item) => categoryFilter === "all" || item.category === categoryFilter,
    )
    .filter(
      (item) =>
        !itemSearch ||
        item.name.toLowerCase().includes(itemSearch.toLowerCase()) ||
        item.description.toLowerCase().includes(itemSearch.toLowerCase()),
    );

  const filteredCategories = categories.filter(
    (cat) =>
      !categorySearch ||
      cat.name.toLowerCase().includes(categorySearch.toLowerCase()),
  );
  const filteredOrders = orders.filter(
    (order) =>
      (!orderStatusFilter || order.status === orderStatusFilter) &&
      (!orderSearch ||
        (order.items &&
          Array.isArray(order.items) &&
          order.items.some((i: any) =>
            i.name?.toLowerCase().includes(orderSearch.toLowerCase()),
          )) ||
        (order.customerName &&
          order.customerName
            .toLowerCase()
            .includes(orderSearch.toLowerCase()))),
  );

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Tabs */}
      <div className="mb-3 border-b border-gray-200 dark:border-gray-700 sm:mb-4">
        <nav className="-mb-px flex space-x-4 overflow-x-auto sm:space-x-8">
          {tabs.map((tab) => (
            <button
              className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-1 py-2 text-xs font-medium sm:gap-2 sm:text-sm ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "items" && (
        <>
          <ConfirmDialog
            cancelText="Cancel"
            confirmText="Delete"
            icon={<Trash2 className="h-7 w-7 text-red-500 dark:text-red-400" />}
            message={`Are you sure you want to delete '${deleteItem?.name}'? This action cannot be undone.`}
            onCancel={() => {
              setShowDeleteConfirm(false);
              setDeleteItem(null);
            }}
            onConfirm={async () => {
              if (!deleteItem || !deleteItem.id) return;
              setShowDeleteConfirm(false);
              setLoadingMenu(true);
              try {
                const MenuService = (await import("@/services/MenuService"))
                  .default;
                await MenuService.deleteMenuItem(deleteItem.id);
                toast.success("Menu item deleted successfully!");
                if (!user?.id) return;
                MenuService.getMenuItemsByUser(user.id)
                  .then((res) => {
                    setMenuItems(
                      res.data.map((item: any) => ({
                        ...item,
                        isAvailable: item.available === 1,
                      })),
                    );
                  })
                  .catch((err) => {
                    console.error(
                      "Failed to fetch menu items after delete:",
                      err,
                    );
                  })
                  .finally(() => setLoadingMenu(false));
                setDeleteItem(null);
              } catch (err) {
                toast.error("Failed to delete menu item");
                setLoadingMenu(false);
              } finally {
                setLoadingMenu(false);
              }
            }}
            open={showDeleteConfirm}
            title="Delete Menu Item"
          />
          <AddItemModal
            categories={categories}
            onClose={() => setShowAddItem(false)}
            onSubmit={(data) => {
              setShowAddItem(false);
              setLoadingMenu(true);
              if (!user?.id) return;
              import("@/services/MenuService").then(
                ({ default: MenuService }) => {
                  MenuService.getMenuItemsByUser(user.id)
                    .then((res: any) => {
                      setMenuItems(
                        res.data.map((item: any) => ({
                          ...item,
                          isAvailable: item.available === 1,
                        })),
                      );
                    })
                    .catch((err: any) => {
                      console.error("Failed to fetch menu items:", err);
                    })
                    .finally(() => setLoadingMenu(false));
                },
              );
            }}
            open={showAddItem}
          />
          {/* Modern details modal for item view */}
          <ItemDetailsModal
            item={viewItem}
            onClose={() => setShowDetails(false)}
            open={showDetails}
          />
          <ItemEditModal
            categories={categories}
            item={editItem}
            onClose={() => setEditItem(null)}
            onSubmit={(data) => {
              setEditItem(null);
              setLoadingMenu(true);
              if (!user?.id) return;
              import("@/services/MenuService").then(
                ({ default: MenuService }) => {
                  MenuService.getMenuItemsByUser(user.id)
                    .then((res: any) => {
                      setMenuItems(
                        res.data.map((item: any) => ({
                          ...item,
                          isAvailable: item.available === 1,
                        })),
                      );
                    })
                    .catch((err: any) => {
                      console.error("Failed to fetch menu items:", err);
                    })
                    .finally(() => setLoadingMenu(false));
                },
              );
            }}
            open={!!editItem}
          />
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
              Menu Items ({menuItems.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              <button
                className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium sm:px-3 sm:text-sm ${itemView === "table" ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"}`}
                onClick={() => setItemView("table")}
              >
                Table
              </button>
              <button
                className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium sm:px-3 sm:text-sm ${itemView === "grid" ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"}`}
                onClick={() => setItemView("grid")}
              >
                Cards
              </button>
              <button
                className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 sm:gap-2 sm:px-4 sm:text-sm"
                onClick={() => setShowAddItem(true)}
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                Add
              </button>
            </div>
          </div>
          <div className="mb-2 flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <select
              className="w-full rounded border px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:w-auto sm:text-sm"
              onChange={(e) => setCategoryFilter(e.target.value)}
              value={categoryFilter}
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option
                  key={cat.id ? String(cat.id) : cat.name}
                  value={cat.name}
                >
                  {cat.name}
                </option>
              ))}
            </select>
            <input
              className="w-full flex-1 rounded border px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:text-sm"
              onChange={(e) => setItemSearch(e.target.value)}
              placeholder="Search menu items..."
              type="text"
              value={itemSearch}
            />
            <button
              className="rounded bg-green-600 px-3 py-1.5 text-xs text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 sm:text-sm"
              onClick={() => {
                const csv = [
                  ["Name", "Category", "Description", "Price", "Available"],
                  ...filteredMenuItems.map((item) => [
                    item.name,
                    item.category,
                    item.description,
                    item.price,
                    item.isAvailable ? "Yes" : "No",
                  ]),
                ]
                  .map((row) => row.join(","))
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "menu_items.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </button>
          </div>
          {itemView === "table" ? (
            <div className="w-full overflow-x-auto">
              {loadingMenu ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  Loading menu items...
                </div>
              ) : filteredMenuItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <FolderOpen
                    className="mb-4 h-24 w-24 text-gray-300 dark:text-gray-600"
                    strokeWidth={1.5}
                  />
                  <div className="text-lg font-semibold text-gray-500 dark:text-gray-400">
                    No menu items found
                  </div>
                  <div className="mb-2 text-sm text-gray-400">
                    Add your first menu item to get started.
                  </div>
                  <button
                    className="mt-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    onClick={() => setShowAddItem(true)}
                  >
                    <Plus className="mr-1 inline-block h-4 w-4" /> Add Item
                  </button>
                </div>
              ) : (
                <table className="w-full rounded-lg bg-white text-xs shadow-md dark:bg-gray-800 sm:text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Available
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Image
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                    {filteredMenuItems.map((item) => (
                      <tr key={item.id}>
                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-gray-100">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 text-gray-900 dark:text-gray-100">
                          {item.category}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                          {item.description}
                        </td>
                        <td className="px-6 py-4 text-gray-900 dark:text-gray-100">
                          R {item.price}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(item.isAvailable ? "active" : "inactive")}`}
                          >
                            {item.isAvailable ? "Available" : "Unavailable"}
                          </span>
                          <button
                            className={`ml-2 rounded px-2 py-1 text-xs ${item.isAvailable ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}
                            onClick={async () => {
                              const MenuService = (
                                await import("@/services/MenuService")
                              ).default;
                              try {
                                const response =
                                  await MenuService.updateAvailability(
                                    item.id,
                                    !item.isAvailable,
                                  );
                                setLoadingMenu(true);
                                if (!user?.id) {
                                  setLoadingMenu(false);
                                  return;
                                }
                                MenuService.getMenuItemsByUser(user.id)
                                  .then((res: any) => {
                                    setMenuItems(
                                      res.data.map((item: any) => ({
                                        ...item,
                                        isAvailable: item.available === 1,
                                      })),
                                    );
                                  })
                                  .catch((err: any) => {
                                    console.error(
                                      "Failed to fetch menu items after update:",
                                      err,
                                    );
                                  })
                                  .finally(() => setLoadingMenu(false));
                              } catch (err) {
                                console.error(
                                  "Failed to update availability:",
                                  err,
                                );
                                alert("Failed to update availability");
                              } finally {
                                setLoadingMenu(false);
                              }
                            }}
                          >
                            {item.isAvailable
                              ? "Set Unavailable"
                              : "Set Available"}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div
                            style={{
                              height: 64,
                              position: "relative",
                              width: 64,
                            }}
                          >
                            {item.images &&
                            Array.isArray(item.images) &&
                            item.images[0] ? (
                              <img
                                alt={item.name}
                                className="h-16 w-16 rounded border bg-white object-contain dark:border-gray-700"
                                src={getImageUrl(item.images[0])}
                                style={{
                                  background: "#fff",
                                  display: "block",
                                  objectFit: "contain",
                                }}
                              />
                            ) : item.image ? (
                              <img
                                alt={item.name}
                                className="h-16 w-16 rounded border bg-white object-contain dark:border-gray-700"
                                src={getImageUrl(item.image)}
                                style={{
                                  background: "#fff",
                                  display: "block",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <div className="flex h-16 w-16 items-center justify-center rounded bg-gray-100 text-xs text-gray-400 dark:bg-gray-900">
                                No image
                              </div>
                            )}
                            <label
                              style={{
                                alignItems: "center",
                                background: "rgba(0,0,0,0.6)",
                                borderRadius: "50%",
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "center",
                                padding: 4,
                                position: "absolute",
                                right: 0,
                                top: 0,
                                zIndex: 2,
                              }}
                              title="Change image"
                            >
                              <Edit className="h-4 w-4 text-white" />
                              <input
                                accept="image/*"
                                name="image"
                                onChange={async (e) => {
                                  const fileInput = e.target;
                                  if (
                                    !fileInput.files ||
                                    fileInput.files.length === 0
                                  ) {
                                    alert("Please select an image file.");
                                    return;
                                  }
                                  const imageFile = fileInput.files[0];
                                  const MenuService = (
                                    await import("@/services/MenuService")
                                  ).default;
                                  try {
                                    await MenuService.updateImage(
                                      item.id,
                                      imageFile,
                                    );
                                    setLoadingMenu(true);
                                    if (!user?.id) {
                                      setLoadingMenu(false);
                                      return;
                                    }
                                    const userId = user.id;
                                    MenuService.getMenuItemsByUser(userId)
                                      .then((res) => {
                                        setMenuItems(
                                          res.data.map(
                                            (item: { available: number }) => ({
                                              ...item,
                                              isAvailable: item.available === 1,
                                            }),
                                          ),
                                        );
                                      })
                                      .catch((err) => {
                                        console.error(
                                          "Failed to fetch menu items after image update:",
                                          err,
                                        );
                                      })
                                      .finally(() => setLoadingMenu(false));
                                  } catch (err) {
                                    console.error(
                                      "Failed to update image:",
                                      err,
                                    );
                                    alert("Failed to update image");
                                  } finally {
                                    setLoadingMenu(false);
                                  }
                                }}
                                style={{
                                  cursor: "pointer",
                                  height: "100%",
                                  left: 0,
                                  opacity: 0,
                                  position: "absolute",
                                  top: 0,
                                  width: "100%",
                                }}
                                type="file"
                              />
                            </label>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                              onClick={() => {
                                setViewItem(item);
                                setShowDetails(true);
                              }}
                              title="View Details"
                            >
                              <svg
                                fill="currentColor"
                                height="20"
                                viewBox="0 0 24 24"
                                width="20"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M12 5c-7.633 0-11 7-11 7s3.367 7 11 7 11-7 11-7-3.367-7-11-7zm0 12c-4.418 0-8-3.582-8-5s3.582-5 8-5 8 3.582 8 5-3.582 5-8 5zm0-8c-1.657 0-3 1.343-3 3s1.343 3 3 3 3-1.343 3-3-1.343-3-3-3z" />
                              </svg>
                            </button>
                            <button
                              className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                              onClick={() => setEditItem(item)}
                              title="Edit Item"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600"
                              onClick={() => {
                                setDeleteItem(item);
                                setShowDeleteConfirm(true);
                              }}
                              title="Delete Item"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {loadingMenu ? (
                <div className="col-span-full p-8 text-center text-gray-500 dark:text-gray-400">
                  Loading menu items...
                </div>
              ) : filteredMenuItems.length === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center py-16">
                  <FolderOpen
                    className="mb-4 h-24 w-24 text-gray-300 dark:text-gray-600"
                    strokeWidth={1.5}
                  />
                  <div className="text-lg font-semibold text-gray-500 dark:text-gray-400">
                    No menu items found
                  </div>
                  <div className="mb-2 text-sm text-gray-400">
                    Add your first menu item to get started.
                  </div>
                  <button
                    className="mt-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    onClick={() => setShowAddItem(true)}
                  >
                    <Plus className="mr-1 inline-block h-4 w-4" /> Add Item
                  </button>
                </div>
              ) : (
                filteredMenuItems.map((item) => (
                  <div
                    className="w-full rounded-lg bg-white p-5 shadow-md dark:bg-gray-800 sm:p-4"
                    key={item.id}
                  >
                    <div style={{ position: "relative", width: "100%" }}>
                      {item.images &&
                      Array.isArray(item.images) &&
                      item.images[0] ? (
                        <img
                          alt={item.name}
                          className="mb-2 w-full rounded border bg-white object-contain dark:border-gray-700"
                          src={getImageUrl(item.images[0])}
                          style={{
                            background: "#fff",
                            display: "block",
                            height: "200px",
                            margin: "0 auto",
                            maxWidth: "100%",
                            objectFit: "contain",
                            width: "100%",
                          }}
                        />
                      ) : item.image ? (
                        <img
                          alt={item.name}
                          className="mb-2 w-full rounded border bg-white object-contain dark:border-gray-700"
                          src={getImageUrl(item.image)}
                          style={{
                            background: "#fff",
                            display: "block",
                            height: "200px",
                            margin: "0 auto",
                            maxWidth: "100%",
                            objectFit: "contain",
                            width: "100%",
                          }}
                        />
                      ) : (
                        <div className="mb-2 flex h-52 w-full items-center justify-center rounded bg-gray-100 text-gray-400 dark:bg-gray-900">
                          No image
                        </div>
                      )}
                      <label
                        style={{
                          alignItems: "center",
                          background: "rgba(0,0,0,0.6)",
                          borderRadius: "50%",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "center",
                          padding: "6px",
                          position: "absolute",
                          right: 8,
                          top: 8,
                          zIndex: 2,
                        }}
                      >
                        {/* Pencil Edit Icon SVG */}
                        <svg
                          fill="white"
                          height="22"
                          viewBox="0 0 24 24"
                          width="22"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25zm14.71-9.04a1.003 1.003 0 0 0 0-1.42l-2.54-2.54a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                        </svg>
                        <input
                          accept="image/*"
                          name="image"
                          onChange={async (e) => {
                            const fileInput = e.target;
                            if (
                              !fileInput.files ||
                              fileInput.files.length === 0
                            ) {
                              alert("Please select an image file.");
                              return;
                            }
                            const imageFile = fileInput.files[0];
                            const MenuService = (
                              await import("@/services/MenuService")
                            ).default;
                            try {
                              await MenuService.updateImage(item.id, imageFile);
                              setLoadingMenu(true);
                              if (!user?.id) {
                                setLoadingMenu(false);
                                return;
                              }
                              const userId = user.id;
                              MenuService.getMenuItemsByUser(userId)
                                .then((res: any) => {
                                  setMenuItems(
                                    res.data.map((item: any) => ({
                                      ...item,
                                      isAvailable: item.available === 1,
                                    })),
                                  );
                                })
                                .catch((err: any) => {
                                  console.error(
                                    "Failed to fetch menu items after image update:",
                                    err,
                                  );
                                })
                                .finally(() => setLoadingMenu(false));
                            } catch (err) {
                              console.error("Failed to update image:", err);
                              alert("Failed to update image");
                            }
                          }}
                          style={{
                            cursor: "pointer",
                            height: "100%",
                            left: 0,
                            opacity: 0,
                            position: "absolute",
                            top: 0,
                            width: "100%",
                          }}
                          type="file"
                        />
                      </label>
                    </div>
                    <div className="text-base font-medium text-gray-900 dark:text-gray-100 sm:text-sm">
                      {item.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {item.category}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {item.description}
                    </div>
                    <div className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-sm">
                      R {item.price}
                    </div>
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(item.isAvailable ? "active" : "inactive")}`}
                    >
                      {item.isAvailable ? "Available" : "Unavailable"}
                    </span>
                    <button
                      className={`ml-2 rounded px-2 py-1 text-xs ${item.isAvailable ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}
                      onClick={async () => {
                        const MenuService = (
                          await import("@/services/MenuService")
                        ).default;
                        try {
                          const response = await MenuService.updateAvailability(
                            item.id,
                            !item.isAvailable,
                          );
                          console.log(
                            "Availability update response:",
                            response,
                          );
                          // Refresh menu items
                          if (!user?.id) return;
                          setLoadingMenu(true);
                          MenuService.getMenuItemsByUser(user.id)
                            .then((res: any) => {
                              setMenuItems(
                                res.data.map((item: any) => ({
                                  ...item,
                                  isAvailable: item.available === 1,
                                })),
                              );
                            })
                            .catch((err: any) => {
                              console.error(
                                "Failed to fetch menu items after update:",
                                err,
                              );
                            })
                            .finally(() => setLoadingMenu(false));
                        } catch (err) {
                          console.error("Failed to update availability:", err);
                          alert("Failed to update availability");
                        }
                      }}
                    >
                      {item.isAvailable ? "Set Unavailable" : "Set Available"}
                    </button>
                    <div className="mt-2 flex gap-2">
                      <button
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                        onClick={() => {
                          setViewItem(item);
                          setShowDetails(true);
                        }}
                        title="View Details"
                      >
                        <svg
                          fill="currentColor"
                          height="20"
                          viewBox="0 0 24 24"
                          width="20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M12 5c-7.633 0-11 7-11 7s3.367 7 11 7 11-7 11-7-3.367-7-11-7zm0 12c-4.418 0-8-3.582-8-5s3.582-5 8-5 8 3.582 8 5-3.582 5-8 5zm0-8c-1.657 0-3 1.343-3 3s1.343 3 3 3 3-1.343 3-3-1.343-3-3-3z" />
                        </svg>
                      </button>
                      <button
                        className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                        onClick={() => setEditItem(item)}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600"
                        onClick={() => {
                          setDeleteItem(item);
                          setShowDeleteConfirm(true);
                        }}
                        title="Delete Item"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {activeTab === "categories" && (
        <>
          <AddCategoryModal
            onClose={() => setShowAddCategory(false)}
            onSubmit={() => {
              toast.success("Category added successfully!");
              setShowAddCategory(false);
              setLoadingCategories(true);
              if (!user?.id) return;
              menuCategoryService
                .getCategories({ uploaded_by: user.id })
                .then((res: any) => {
                  setCategories(res.data);
                })
                .catch((err: any) => {
                  console.error("Failed to fetch categories:", err);
                })
                .finally(() => setLoadingCategories(false));
            }}
            open={showAddCategory}
          />
          <CategoryViewModal
            category={viewCategory}
            onClose={() => setViewCategory(null)}
            open={!!viewCategory}
          />
          <CategoryEditModal
            category={editCategory}
            onClose={() => setEditCategory(null)}
            onSubmit={async (data) => {
              if (!editCategory || !editCategory.id) return;
              setLoadingCategories(true);
              try {
                await menuCategoryService.updateCategory(editCategory.id, {
                  description: data.description,
                  name: data.name,
                });
                toast.success("Category updated successfully!");
                // Refresh categories
                if (!user?.id) return;
                const res = await menuCategoryService.getCategories({
                  uploaded_by: user.id,
                });
                setCategories(res.data);
                setEditCategory(null);
              } catch (err) {
                toast.error("Failed to update category");
                setLoadingCategories(false);
              } finally {
                setLoadingCategories(false);
              }
            }}
            open={!!editCategory}
          />
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
              Categories ({categories.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              <button
                className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium sm:px-3 sm:text-sm ${categoryView === "table" ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"}`}
                onClick={() => setCategoryView("table")}
              >
                Table
              </button>
              <button
                className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium sm:px-3 sm:text-sm ${categoryView === "grid" ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"}`}
                onClick={() => setCategoryView("grid")}
              >
                Cards
              </button>
              <button
                className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 sm:gap-2 sm:px-4 sm:text-sm"
                onClick={() => setShowAddCategory(true)}
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                Add
              </button>
            </div>
          </div>
          <div className="mb-2 flex w-full flex-col gap-2 sm:flex-row sm:items-center">
            <input
              className="w-full flex-1 rounded border px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:text-sm"
              onChange={(e) => setCategorySearch(e.target.value)}
              placeholder="Search categories..."
              type="text"
              value={categorySearch}
            />
            <button
              className="rounded bg-green-600 px-3 py-1.5 text-xs text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 sm:text-sm"
              onClick={() => {
                const csv = [
                  ["Category"],
                  ...filteredCategories.map((cat) => [cat.name]),
                ]
                  .map((row) => row.join(","))
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "categories.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </button>
          </div>
          {categoryView === "table" ? (
            <div className="w-full overflow-x-auto">
              {loadingCategories ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  Loading categories...
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <FolderPlus
                    className="mb-4 h-24 w-24 text-gray-300 dark:text-gray-600"
                    strokeWidth={1.5}
                  />
                  <div className="text-lg font-semibold text-gray-500 dark:text-gray-400">
                    No categories found
                  </div>
                  <div className="mb-2 text-sm text-gray-400">
                    Add your first category to get started.
                  </div>
                  <button
                    className="mt-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    onClick={() => setShowAddCategory(true)}
                  >
                    <Plus className="mr-1 inline-block h-4 w-4" /> Add Category
                  </button>
                </div>
              ) : (
                <table className="w-full rounded-lg bg-white text-xs shadow-md dark:bg-gray-800 sm:text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Created At
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                    {filteredCategories.map((cat) => (
                      <tr key={cat.id || cat.name}>
                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-gray-100">
                          {cat.name}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                          {cat.description || (
                            <span className="italic text-gray-400">
                              No description
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-gray-500 dark:text-gray-400">
                          {cat.created_at ? (
                            new Date(cat.created_at).toLocaleString()
                          ) : (
                            <span className="italic text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                              onClick={() => setEditCategory(cat)}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600"
                              onClick={() => {
                                setDeleteCategory(cat);
                                setShowDeleteCategoryConfirm(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {loadingCategories ? (
                <div className="col-span-full p-8 text-center text-gray-500 dark:text-gray-400">
                  Loading categories...
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center py-16">
                  <FolderPlus
                    className="mb-4 h-24 w-24 text-gray-300 dark:text-gray-600"
                    strokeWidth={1.5}
                  />
                  <div className="text-lg font-semibold text-gray-500 dark:text-gray-400">
                    No categories found
                  </div>
                  <div className="mb-2 text-sm text-gray-400">
                    Add your first category to get started.
                  </div>
                  <button
                    className="mt-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    onClick={() => setShowAddCategory(true)}
                  >
                    <Plus className="mr-1 inline-block h-4 w-4" /> Add Category
                  </button>
                </div>
              ) : (
                filteredCategories.map((cat) => (
                  <div
                    className="w-full rounded-lg bg-white p-5 shadow-md dark:bg-gray-800 sm:p-4"
                    key={cat.id || cat.name}
                  >
                    <h4 className="text-base font-medium text-gray-900 dark:text-gray-100 sm:text-sm">
                      {cat.name}
                    </h4>
                    <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {cat.description || (
                        <span className="italic text-gray-400">
                          No description
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                      {cat.created_at ? (
                        `Created: ${new Date(cat.created_at).toLocaleString()}`
                      ) : (
                        <span className="italic">-</span>
                      )}
                    </div>
                    <div className="mt-2 flex gap-2">
                      <button
                        className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                        onClick={() => setEditCategory(cat)}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600"
                        onClick={() => {
                          setDeleteCategory(cat);
                          setShowDeleteCategoryConfirm(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          <ConfirmDialog
            cancelText="Cancel"
            confirmText="Delete"
            icon={<Trash2 className="h-7 w-7 text-red-500 dark:text-red-400" />}
            message={`Are you sure you want to delete '${deleteCategory?.name}'? This action cannot be undone.`}
            onCancel={() => {
              setShowDeleteCategoryConfirm(false);
              setDeleteCategory(null);
            }}
            onConfirm={async () => {
              if (!deleteCategory || !deleteCategory.id) return;
              setShowDeleteCategoryConfirm(false);
              setLoadingCategories(true);
              try {
                await menuCategoryService.deleteCategory(deleteCategory.id);
                toast.success("Category deleted successfully!");
                // Refresh categories
                if (!user?.id) return;
                const res = await menuCategoryService.getCategories({
                  uploaded_by: user.id,
                });
                setCategories(res.data);
                setDeleteCategory(null);
              } catch (err) {
                toast.error("Failed to delete category");
                setLoadingCategories(false);
              } finally {
                setLoadingCategories(false);
              }
            }}
            open={showDeleteCategoryConfirm}
            title="Delete Category"
          />
        </>
      )}

      {activeTab === "orders" && (
        <>
          <AddOrderModal
            onClose={() => setShowAddOrder(false)}
            onSubmit={async (data) => {
              setShowAddOrder(false);
              setLoadingOrders(true);
              try {
                if (!user?.id) return;
                await orderService
                  .getOrders({ created_by: user.id })
                  .then((res: any) => setOrders(res.data));
              } catch (err) {
                toast.error("Failed to refresh orders");
              } finally {
                setLoadingOrders(false);
              }
            }}
            open={showAddOrder}
          />
          <OrderViewModal
            onClose={() => setViewOrder(null)}
            open={!!viewOrder}
            order={viewOrder}
          />
          <OrderEditModal
            onClose={() => setEditOrder(null)}
            onSubmit={async (data) => {
              setLoadingOrders(true);
              try {
                await orderService.updateOrder(editOrder.id, data);
                toast.success("Order updated successfully!");
                if (!user?.id) return;
                await orderService
                  .getOrders({ created_by: user.id })
                  .then((res: any) => setOrders(res.data));
              } catch (err) {
                toast.error("Failed to update order");
              } finally {
                setLoadingOrders(false);
                setEditOrder(null);
              }
            }}
            open={!!editOrder}
            order={editOrder}
          />
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
              Orders ({orders.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              <button
                className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium sm:px-3 sm:text-sm ${
                  orderView === "table"
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                }`}
                onClick={() => setOrderView("table")}
              >
                Table
              </button>
              <button
                className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium sm:px-3 sm:text-sm ${
                  orderView === "grid"
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                }`}
                onClick={() => setOrderView("grid")}
              >
                Cards
              </button>
              <button
                className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 sm:gap-2 sm:px-4 sm:text-sm"
                onClick={() => setShowAddOrder(true)}
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                Add
              </button>
            </div>
          </div>
          <div className="mb-2 flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <select
              className="w-full rounded border px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:w-auto sm:text-sm"
              onChange={(e) => setOrderStatusFilter(e.target.value)}
              value={orderStatusFilter}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="preparing">Preparing</option>
              <option value="ready">Ready for Pickup</option>
              <option value="delivering">Out for Delivery</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <input
              className="w-full flex-1 rounded border px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 sm:text-sm"
              onChange={(e) => setOrderSearch(e.target.value)}
              placeholder="Search orders..."
              type="text"
              value={orderSearch}
            />
            <button
              className="rounded bg-green-600 px-3 py-1.5 text-xs text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 sm:text-sm"
              onClick={() => {
                const csv = [
                  ["Items", "Status", "Customer"],
                  ...filteredOrders.map((order) => [
                    order.items && Array.isArray(order.items)
                      ? order.items
                          .map((i: any) => i.name || i.itemId)
                          .join(" | ")
                      : "",
                    order.status,
                    order.customerName,
                  ]),
                ]
                  .map((row) => row.join(","))
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "orders.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </button>
          </div>
          {orderView === "table" ? (
            <div className="w-full overflow-x-auto">
              {loadingOrders ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  Loading orders...
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <FileQuestion
                    className="mb-4 h-24 w-24 text-gray-300 dark:text-gray-600"
                    strokeWidth={1.5}
                  />
                  <div className="text-lg font-semibold text-gray-500 dark:text-gray-400">
                    No orders found
                  </div>
                  <div className="mb-2 text-sm text-gray-400">
                    Add your first order to get started.
                  </div>
                  <button
                    className="mt-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    onClick={() => setShowAddOrder(true)}
                  >
                    <Plus className="mr-1 inline-block h-4 w-4" /> Add Order
                  </button>
                </div>
              ) : (
                <table className="w-full rounded-lg bg-white text-xs shadow-md dark:bg-gray-800 sm:text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Items
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-200">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                    {filteredOrders.map((order) => (
                      <tr key={order.id}>
                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-gray-100">
                          {order.items && Array.isArray(order.items)
                            ? order.items
                                .map((i: any) => i.name || i.itemId)
                                .join(", ")
                            : ""}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                          {order.status}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                          {order.customerName || order.customer_name}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                              onClick={() => setEditOrder(order)}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600"
                              onClick={() => {
                                setDeleteOrder(order);
                                setShowDeleteOrderConfirm(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {loadingOrders ? (
                <div className="col-span-full p-8 text-center text-gray-500 dark:text-gray-400">
                  Loading orders...
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center py-16">
                  <FileQuestion
                    className="mb-4 h-24 w-24 text-gray-300 dark:text-gray-600"
                    strokeWidth={1.5}
                  />
                  <div className="text-lg font-semibold text-gray-500 dark:text-gray-400">
                    No orders found
                  </div>
                  <div className="mb-2 text-sm text-gray-400">
                    Add your first order to get started.
                  </div>
                  <button
                    className="mt-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    onClick={() => setShowAddOrder(true)}
                  >
                    <Plus className="mr-1 inline-block h-4 w-4" /> Add Order
                  </button>
                </div>
              ) : (
                filteredOrders.map((order) => (
                  <div
                    className="w-full rounded-lg bg-white p-5 shadow-md dark:bg-gray-800 sm:p-4"
                    key={order.id}
                  >
                    <h4 className="text-base font-medium text-gray-900 dark:text-gray-100 sm:text-sm">
                      {order.items && Array.isArray(order.items)
                        ? order.items
                            .map((i: any) => i.name || i.itemId)
                            .join(", ")
                        : ""}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Status: {order.status}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Customer: {order.customerName || order.customer_name}
                    </p>
                    <div className="mt-2 flex gap-2">
                      <button
                        className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                        onClick={() => setEditOrder(order)}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-600"
                        onClick={() => {
                          setDeleteOrder(order);
                          setShowDeleteOrderConfirm(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        cancelText="Cancel"
        confirmText="Delete"
        icon={<Trash2 className="h-7 w-7 text-red-500 dark:text-red-400" />}
        message={`Are you sure you want to delete this order? This action cannot be undone.`}
        onCancel={() => {
          setShowDeleteOrderConfirm(false);
          setDeleteOrder(null);
        }}
        onConfirm={async () => {
          if (!deleteOrder) return;
          setShowDeleteOrderConfirm(false);
          try {
            await orderService.deleteOrder(deleteOrder.id);
            toast.success("Order deleted successfully!");
            setLoadingOrders(true);
            if (!user?.id) return;
            orderService
              .getOrders({ created_by: user.id })
              .then((res: any) => {
                setOrders(res.data);
              })
              .catch((err: any) => {
                console.error("Failed to fetch orders after delete:", err);
              })
              .finally(() => setLoadingOrders(false));
            setDeleteOrder(null);
          } catch (err) {
            console.error("Failed to delete order:", err);
            toast.error("Failed to delete order");
          }
        }}
        open={showDeleteOrderConfirm}
        title="Delete Order"
      />
    </div>
  );
};

export default RestaurantManagement;
